// VX sales → $ 매출 집계 (순수 함수, I/O 없음). 100 VX = $1 peg.

const VX_PER_USD = 100;
const toUsd = (vx: number): number => vx / VX_PER_USD;
const TOP_PAYERS_LIMIT = 50;

/** 집계에 필요한 sales 레코드 최소 형태. meme-api VxSale 의 구조적 부분집합. */
export interface Sale {
  id: number;
  userUid: string;
  shopItemId: number;
  totalPrice: number; // VX
  status?: string;
  createdAt: string; // ISO UTC
  refundedAt?: string | null;
  item?: { productId?: string; name?: string };
  // displayName = V8 프로필 표시명(닉네임), handle = @핸들, name = 계정 이름(있으면).
  buyer?: { walletAddress?: string; displayName?: string; handle?: string; name?: string | null };
}

/** 환불 판정 — refundedAt/status/refundStatus/refund 객체 방어. (구 admin isRefunded 이식) */
export function isRefunded(sale: Record<string, unknown> | null | undefined): boolean {
  if (!sale || typeof sale !== 'object') return false;
  if (sale.refundedAt) return true;
  if (sale.refundDate) return true;
  if (sale.isRefunded === true) return true;
  if (typeof sale.status === 'string' && /refund/i.test(sale.status)) return true;
  if (
    typeof sale.refundStatus === 'string' &&
    sale.refundStatus !== '' &&
    (sale.refundStatus as string).toUpperCase() !== 'NONE'
  )
    return true;
  if (sale.refund && typeof sale.refund === 'object') return true;
  return false;
}

export const isRefundedSale = (s: Sale): boolean =>
  s.refundedAt != null || isRefunded(s as unknown as Record<string, unknown>);

/** 상품 집계 키 — productId 우선, 없으면 shopItemId. (split 집계가 같은 키를 쓰도록 공유) */
export const productKeyOf = (s: Sale): string => s.item?.productId ?? String(s.shopItemId);

/** 일별 집계 키 — createdAt 의 UTC 날짜(YYYY-MM-DD). */
export const dayKeyOf = (s: Sale): string => s.createdAt.slice(0, 10);

export interface RevenueAgg {
  grossUsd: number;
  netUsd: number;
  refundUsd: number;
  count: number;
  refundCount: number;
  refundRate: number;
  payers: number;
  arppuUsd: number; // netUsd / payers
  byProduct: { productId: string; name: string; count: number; grossUsd: number; netUsd: number }[];
  byDay: { date: string; count: number; grossUsd: number; netUsd: number }[];
  // 순매출(net) 기준 상위 과금 지갑 Top N. wallet 없으면 UI 에서 displayName/uid fallback.
  // displayName/handle/name 은 결제 레코드에 실린 V8 프로필 — 없으면 빈 문자열.
  topPayers: {
    uid: string;
    wallet: string;
    displayName: string;
    handle: string;
    name: string;
    count: number;
    netUsd: number;
  }[];
}

// 누적은 정수 VX(totalPrice) 로만 — 부동소수 USD 합산 drift 방지. USD 변환은 출력 직전 1회.
interface VxProductAcc {
  productId: string;
  name: string;
  count: number;
  grossVx: number;
  netVx: number;
}
interface VxDayAcc {
  date: string;
  count: number;
  grossVx: number;
  netVx: number;
}
interface VxPayerAcc {
  uid: string;
  wallet: string;
  displayName: string;
  handle: string;
  name: string;
  count: number;
  netVx: number;
}

export function aggregateSales(sales: Sale[]): RevenueAgg {
  let grossVx = 0;
  let netVx = 0;
  let refundVx = 0;
  let count = 0;
  let refundCount = 0;
  const payerSet = new Set<string>();
  const prod = new Map<string, VxProductAcc>();
  const day = new Map<string, VxDayAcc>();
  const payer = new Map<string, VxPayerAcc>();

  for (const s of sales) {
    const vx = s.totalPrice;
    const refunded = isRefundedSale(s);
    grossVx += vx;
    count++;
    if (refunded) {
      refundVx += vx;
      refundCount++;
    } else {
      netVx += vx;
      payerSet.add(s.userUid);
      // payer 별 순지출 누적 (환불 제외). 프로필 필드는 보이는 값으로 갱신.
      const pa = payer.get(s.userUid) ?? {
        uid: s.userUid,
        wallet: '',
        displayName: '',
        handle: '',
        name: '',
        count: 0,
        netVx: 0,
      };
      pa.count++;
      pa.netVx += vx;
      if (s.buyer?.walletAddress) pa.wallet = s.buyer.walletAddress;
      if (s.buyer?.displayName) pa.displayName = s.buyer.displayName;
      if (s.buyer?.handle) pa.handle = s.buyer.handle;
      if (s.buyer?.name) pa.name = s.buyer.name;
      payer.set(s.userUid, pa);
    }

    const pk = productKeyOf(s);
    const p = prod.get(pk) ?? {
      productId: pk,
      name: s.item?.name ?? pk,
      count: 0,
      grossVx: 0,
      netVx: 0,
    };
    p.count++;
    p.grossVx += vx;
    if (!refunded) p.netVx += vx;
    prod.set(pk, p);

    const d = dayKeyOf(s); // UTC date
    const dd = day.get(d) ?? { date: d, count: 0, grossVx: 0, netVx: 0 };
    dd.count++;
    dd.grossVx += vx;
    if (!refunded) dd.netVx += vx;
    day.set(d, dd);
  }

  const payers = payerSet.size;
  return {
    grossUsd: toUsd(grossVx),
    netUsd: toUsd(netVx),
    refundUsd: toUsd(refundVx),
    count,
    refundCount,
    refundRate: count ? refundCount / count : 0,
    payers,
    arppuUsd: payers ? toUsd(netVx) / payers : 0,
    byProduct: [...prod.values()]
      .sort((a, b) => b.netVx - a.netVx)
      .map((p) => ({
        productId: p.productId,
        name: p.name,
        count: p.count,
        grossUsd: toUsd(p.grossVx),
        netUsd: toUsd(p.netVx),
      })),
    byDay: [...day.values()]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => ({
        date: d.date,
        count: d.count,
        grossUsd: toUsd(d.grossVx),
        netUsd: toUsd(d.netVx),
      })),
    topPayers: [...payer.values()]
      .sort((a, b) => b.netVx - a.netVx || a.uid.localeCompare(b.uid))
      .slice(0, TOP_PAYERS_LIMIT)
      .map((p) => ({
        uid: p.uid,
        wallet: p.wallet,
        displayName: p.displayName,
        handle: p.handle,
        name: p.name,
        count: p.count,
        netUsd: toUsd(p.netVx),
      })),
  };
}
